import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FormattedMessage, FormattedDate } from 'react-intl';

import Chip from '../brand/Chip';
import Hyperlink from '../common/Hyperlink';
import {
  Section,
  Inner,
  SectionTitle,
  Cover,
  HeadlineRow,
  ViewAllLink,
  imageHover,
} from './shared';
import { media } from '../../styles/style-utils';
import { type } from '../../styles/typography';

const Wrapper = styled(Section)`
  padding-top: 64px;
  padding-bottom: 72px;
  background-color: var(--b-lavender-grey);
  color: var(--b-black);

  ${media.large`
    padding-top: 66px;
    padding-bottom: 98px;
  `};
`;

const Content = styled(Inner)`
  display: flex;
  flex-direction: column;
  gap: 40px;

  ${media.large`
    gap: 56px;
  `};
`;

const Chips = styled.div`
  display: flex;
  gap: 3px;
`;

const Grid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Feature = styled(Hyperlink)`
  display: grid;
  grid-template-columns: 1fr;
  overflow: hidden;
  background-color: var(--b-white);
  color: var(--b-black);
  text-decoration: none !important;
  ${imageHover};

  ${media.large`
    grid-template-columns: 1fr 582px;
  `};

  &:hover .read-more {
    color: var(--b-black);
    text-decoration: underline;
    text-underline-offset: 3px;
  }

  .image {
    position: relative;
    height: 280px;

    ${media.medium`
      height: 372px;
    `}

    ${media.large`
      height: auto;
      min-height: 467px;
    `};

    img {
      ${media.large`
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
      `};
    }
  }

  .text {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: 26px;
    padding: 32px 20px;

    .body {
      display: flex;
      flex-direction: column;
      gap: 26px;
    }

    h3 {
      ${type('headline', 'medium', { emphasis: true })};
      margin: 0;
      padding: 0;
      color: var(--b-black);
    }

    p {
      ${type('body', 'large')};
      margin: 0;
      color: rgba(0, 0, 0, 0.72);
    }

    .read-more {
      ${type('label', 'small', { uppercase: true })};
      margin: 0;
      color: rgba(0, 0, 0, 0.72);
    }
  }
`;

const Cards = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 4px;

  ${media.large`
    grid-template-columns: repeat(3, minmax(0, 1fr));
  `};
`;

const Card = styled(Hyperlink)`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background-color: var(--b-white);
  color: var(--b-black);
  text-decoration: none !important;
  ${imageHover};

  ${media.large`
    min-height: 576px;
  `};

  .image {
    height: 280px;

    ${media.medium`
      height: 372px;
    `};

    background-color: var(--b-black);
  }

  .text {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 26px;
    padding: 24px 20px;

    h3 {
      ${type('headline', 'small', { emphasis: true })};
      margin: 0;
      padding: 0;
      color: var(--b-black);
    }
  }
`;

const articleShape = PropTypes.shape({
  slug: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  image: PropTypes.string.isRequired,
  date: PropTypes.string.isRequired,
  kind: PropTypes.string.isRequired,
  excerpt: PropTypes.string,
});

const DateChip = ({ date }) => (
  <Chip tone="fog" uppercase>
    <FormattedDate value={date} year="numeric" month="long" />
  </Chip>
);

DateChip.propTypes = {
  date: PropTypes.string.isRequired,
};

const Magazine = ({ articles }) => {
  if (!articles || !articles.length) {
    return null;
  }

  const [feature, ...cards] = articles;

  return (
    <Wrapper>
      <Content>
        <HeadlineRow>
          <SectionTitle>
            <FormattedMessage
              id="home.magazine.title"
              defaultMessage="The Stories Behind the Sound"
            />
          </SectionTitle>
          <ViewAllLink to="/magazine" />
        </HeadlineRow>
        <Grid>
          <Feature to={`/magazine/${feature.slug}`}>
            <div className="image">
              <Cover src={feature.image} alt="" />
            </div>
            <div className="text">
              <div className="body">
                <Chips>
                  <Chip tone="fog" pill uppercase>
                    {feature.kind}
                  </Chip>
                  <DateChip date={feature.date} />
                </Chips>
                <h3>{feature.title}</h3>
                {feature.excerpt && <p>{feature.excerpt}</p>}
              </div>
              <p className="read-more">Read the full interview here</p>
            </div>
          </Feature>
          <Cards>
            {cards.map(({ image, kind, date, title, slug }) => (
              <Card to={`/magazine/${slug}`} key={slug}>
                <div className="image">
                  <Cover src={image} alt="" />
                </div>
                <div className="text">
                  <Chips>
                    <Chip tone="fog" pill uppercase>
                      {kind}
                    </Chip>
                    <DateChip date={date} />
                  </Chips>
                  <h3>{title}</h3>
                </div>
              </Card>
            ))}
          </Cards>
        </Grid>
      </Content>
    </Wrapper>
  );
};

Magazine.propTypes = {
  articles: PropTypes.arrayOf(articleShape),
};

export default Magazine;



// WEBPACK FOOTER //
// /tmp/build_2cb81483/src/components/home/Magazine.js